import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, AlertTriangle } from 'lucide-react';
import { trackOrganicLanding } from '../lib/pixel';
import { logger } from '../lib/logger';

/**
 * /o/:token — Organic-Lead landing.
 *
 * Quando un agente registra un lead "organico" nel CRM, il sistema invia
 * al cliente un'email con un link tipo:
 *
 *   https://greenparadisehcf.info/o/<HMAC-token>
 *
 * Questa pagina:
 *   1. Valida il token via GET /api/public/organic/<token>
 *   2. Riceve i dati pre-compilati per il Form 1 (name, email, phone) +
 *      lead_id originale + nome dell'agente che ha portato il lead
 *   3. Li salva in sessionStorage così che:
 *        - VslGate parta normalmente (il cliente guarda il VSL)
 *        - QualificationModal pre-popola il contact step con i dati
 *        - Form 2 submit aggancia il lead esistente (evita duplicati)
 *   4. Redirige a "/" (landing) dove parte il VSL Gate
 *
 * Sessione: i dati prefill sono effimeri (sessionStorage), cancellati al
 * tab close. Se il cliente riapre il link, fa una nuova fetch.
 */

export const ORGANIC_PREFILL_KEY = 'gp_organic_prefill';
export const ORGANIC_LEAD_ID_KEY = 'gp_organic_lead_id';

export default function OrganicMagicLinkPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setError('Link mancante. Verifica di aver aperto il link completo che ti è stato inviato via email.');
        return;
      }
      try {
        const r = await api.get(`/public/organic/${token}`);
        if (cancelled) return;
        const data = r.data || {};
        if (!data.ok || !data.lead_id) {
          throw new Error('invalid');
        }
        // Persist prefill data for the qualification modal
        try {
          sessionStorage.setItem(
            ORGANIC_PREFILL_KEY,
            JSON.stringify(data.prefill || {}),
          );
          sessionStorage.setItem(ORGANIC_LEAD_ID_KEY, data.lead_id);
          if (data.organic_owner_name) {
            sessionStorage.setItem('gp_organic_owner_name', data.organic_owner_name);
          }
        } catch (storageErr) {
          // sessionStorage may be unavailable in restricted browsers — proceed anyway
          logger.debug('OrganicMagicLink: sessionStorage write failed', storageErr);
        }
        // ★ Meta Pixel: OrganicLanding (audience: leads coming from sales emails).
        try { trackOrganicLanding({ ownerId: data.organic_owner_id }); }
        catch (err) { logger.debug('OrganicMagicLink: pixel trackOrganicLanding failed', err); }
        // Redirect to landing (VSL Gate will kick in)
        nav('/', { replace: true });
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 410) {
          setError('Questo link è scaduto. Scrivici su WhatsApp e te ne mandiamo uno nuovo.');
        } else if (status === 404) {
          setError('Lead non trovato. Il link potrebbe essere stato rigenerato. Contattaci su WhatsApp.');
        } else {
          setError('Link non valido. Verifica di averlo copiato correttamente dall\'email.');
        }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token, nav]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-3">
            <AlertTriangle className="w-10 h-10 mx-auto text-amber-600" />
            <h1 className="text-lg font-semibold text-foreground">Link non valido</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <a
              href="https://wa.me/393519801113?text=Ciao%2C%20il%20link%20personale%20che%20mi%20hai%20mandato%20non%20funziona"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 px-4 py-2 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700"
              data-testid="organic-fallback-wa"
            >
              Scrivici su WhatsApp →
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Apriamo il tuo accesso personale…</span>
      </div>
    </div>
  );
}
